import React from "react"
import styled from "styled-components"
import { ButterCmsComponent, PageHeader, ContentTitle, ContentText } from "../types"
import { Content } from "../types/discovery/Content/Content"
import { MediaUrl } from "../types/discovery/Content/MediaUrl/MediaUrl"
import { ErrorBox } from "./ErrorBox"
import { HtmlContainer } from "./HtmlContainer"
import { MediaUrlRenderer } from "./MediaUrlRenderer/MediaUrlRenderer"
import { NarrowPageContent } from "./NarrowPageContent"
import { SectionTitle } from "./SectionTitle"
import { SubtitleAndTitle } from "./SubtitleAndTitle"

type ComponentCandidate =
  | {
      type: string
      fields: Record<string, any>
    }
  | Record<string, any>

export type ButterCmsComponentRendererProps = {
  componentData: ComponentCandidate
  keyName?: string
}

export const ButterCmsComponentRenderer = (props: ButterCmsComponentRendererProps) => {
  const { keyName, componentData } = props

  const componentName = getComponentName(componentData, keyName)

  // Not dynamic component (ignore them for the moment)

  if (ButterCmsComponent.ExpandComponent === componentName) {
    return null
  }

  // Root

  if (ButterCmsComponent.PageHeader === componentName) {
    const pageHeaderData = componentData as PageHeader
    const { subtitle, title, icon_url } = pageHeaderData

    return <SubtitleAndTitle subtitle={subtitle ?? ""} title={title} bgImage={icon_url} />
  }

  if (ButterCmsComponent.Content === componentName) {
    const contentData = componentData as Content
    const children = contentData.map((data, index) => <ButterCmsComponentRenderer key={index} componentData={data} />)

    return <NarrowPageContent>{children}</NarrowPageContent>
  }

  // Partials

  if (ButterCmsComponent.ContentTitle === componentName) {
    const contentTitleData = componentData as ContentTitle

    return <SectionTitle>{contentTitleData.fields.value}</SectionTitle>
  }

  if (ButterCmsComponent.ContentText === componentName) {
    const contentTextData = componentData as ContentText

    return <HtmlContainer dangerouslySetInnerHTML={{ __html: contentTextData.fields.text }} />
  }

  if (ButterCmsComponent.MediaUrl === componentName) {
    const mediaUrlData = componentData as MediaUrl

    return (
      <PreventMultipleComponentPerLine>
        <MediaUrlRenderer type={mediaUrlData.fields.type} url={mediaUrlData.fields.url} />
      </PreventMultipleComponentPerLine>
    )
  }

  return (
    <ErrorBox>
      UNKNOWN BUTTERCMS COMPONENT:
      <br />
      key: {keyName}
      <br />
      data: {componentData}
    </ErrorBox>
  )
}

const PreventMultipleComponentPerLine = styled.div``

const getComponentName = (component: ComponentCandidate, keyName?: string): ButterCmsComponent | undefined => {
  if (!!keyName) return keyName as ButterCmsComponent
  if (!!component.type) return component.type
}
